import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

/**
 * Retailer account dropdown (current production).
 *
 * Ported from `HeaderAccount` / `RevampedDropdownOptions` in
 * `@faire/marketplace-app-chrome`: a greeting header, then the
 * account destinations grouped by section rules, ending with "Sign out".
 * Admin-only impersonation links are intentionally left out.
 *
 * Render it through `RetailerLayout`'s `accountDropdown` render prop so the
 * global nav anchors it under the account glyph and handles open / close.
 */

export type RetailerAccountMenuItemKey =
  | "orders"
  | "invoices"
  | "messages"
  | "reviews"
  | "saved-products"
  | "brand-list"
  | "pos-integration"
  | "team"
  | "settings"
  | "payment-terms"
  | "insider"
  | "refer-brands"
  | "help-center"
  | "sign-out";

export interface RetailerAccountMenuProps {
  /** Retailer's first name for the "Hi, {name}" header. */
  firstName?: string;
  /** Called with the item's key. Navigation is the caller's responsibility. */
  onItemClick?: (itemKey: RetailerAccountMenuItemKey) => void;
  /** Override the "Saved products" label (production A/B's "Favorites"). */
  savedProductsLabel?: string;
}

interface MenuItem {
  key: RetailerAccountMenuItemKey;
  label: string;
  /** Secondary line under the label, e.g. "None overdue". */
  detail?: string;
}

const RetailerAccountMenu: React.FC<RetailerAccountMenuProps> = ({
  firstName = "Chris",
  onItemClick,
  savedProductsLabel = "Saved products",
}) => {
  const accountItems: MenuItem[] = [
    { key: "orders", label: "Orders" },
    { key: "invoices", label: "Invoices", detail: "None overdue" },
    { key: "messages", label: "Messages" },
    { key: "reviews", label: "Reviews" },
    { key: "saved-products", label: savedProductsLabel },
    { key: "brand-list", label: "Brand list" },
    { key: "pos-integration", label: "Point of sale integration" },
    { key: "team", label: "Team & permissions" },
    { key: "settings", label: "Settings" },
  ];
  const programItems: MenuItem[] = [
    { key: "insider", label: "Insider" },
    { key: "refer-brands", label: "Refer brands to earn $2,000" },
  ];

  const renderItem = (item: MenuItem) => (
    <button
      key={item.key}
      type="button"
      role="menuitem"
      data-test-id={`account-menu-${item.key}`}
      onClick={() => onItemClick?.(item.key)}
      className={menuItemStyle.cls()}
    >
      <span className="type-fs-paragraph">{item.label}</span>
      {item.detail ? (
        <span className="type-fs-label text-fs-text-subdued">
          {item.detail}
        </span>
      ) : null}
    </button>
  );

  return (
    <div
      role="menu"
      data-test-id="header-account-dropdown"
      className={menuStyle.cls()}
    >
      <div className="border-fs-border-muted border-b p-4">
        <span className="type-fs-subheading-medium text-fs-text-primary">
          Hi, {firstName}
        </span>
      </div>

      <div className={sectionStyle.cls()}>{accountItems.map(renderItem)}</div>

      <SectionRule />
      <div className={sectionStyle.cls()}>
        <button
          type="button"
          role="menuitem"
          data-test-id="account-menu-payment-terms"
          onClick={() => onItemClick?.("payment-terms")}
          className={menuItemStyle.cls()}
        >
          <span className="type-fs-paragraph">Payment terms</span>
          <span className="type-fs-label text-fs-text-subdued">
            As a new store, you qualify for up to{" "}
            <span className="type-fs-label-medium">$20,000</span> in terms.{" "}
            <span className="text-fs-text-primary underline">
              Apply in seconds
            </span>
          </span>
        </button>
      </div>

      <SectionRule />
      <div className={sectionStyle.cls()}>{programItems.map(renderItem)}</div>

      <SectionRule />
      <div className={sectionStyle.cls()}>
        {renderItem({ key: "help-center", label: "Help center" })}
      </div>

      <SectionRule />
      <div className={sectionStyle.cls()}>
        {renderItem({ key: "sign-out", label: "Sign out" })}
      </div>
    </div>
  );
};

export default RetailerAccountMenu;

const SectionRule: React.FC = () => (
  <span className="bg-fs-border-muted my-4 block h-px w-full" aria-hidden />
);

const menuStyle = tsv({
  base: [
    "bg-fs-surface-primary text-fs-text-primary",
    "flex w-[288px] flex-col rounded pb-4 text-left",
    "shadow-[0_2px_12px_rgba(0,0,0,0.15)]",
  ],
});

const sectionStyle = tsv({
  base: ["flex w-full flex-col items-start gap-3 px-4 pt-3"],
});

const menuItemStyle = tsv({
  base: [
    "text-fs-text-primary flex w-full flex-col items-start gap-0.5 text-left",
    "hover:underline",
  ],
});
